// db.js — frontend data-access wrapper (client half of the Cloud SQL keystone).
//
// Presents a supabase-js-like chainable surface (`db.from(table)...`) so call
// sites can swap `supabase` → `db` with no behavior change. As the chain builds,
// it is captured into a plain `spec` object. A single `transport(spec)` runs it.
//
// v1: transport ONLY delegates to the existing Supabase client — it rebuilds the
// equivalent `supabase.from()` chain and returns its `{ data, error, count, ... }`
// verbatim. This is deliberately behavior-identical: no Cloud SQL yet.
//
// The `spec` shape is intentionally the CONTRACT the backend `db-query` endpoint
// will consume in the next step. Keep it clean and serializable (plain JSON):
//
//   {
//     table:     string,
//     op:        'select' | 'insert' | 'update' | 'delete' | 'upsert',
//     columns:   string | undefined,   // arg to .select(); undefined = no read-back
//     filters:   [{ col, operator, value, negate? }],
//                //   operator ∈ eq|neq|in|gt|gte|lt|lte|like|ilike|is
//                //   negate:true represents .not(col, operator, value)
//     order:     [{ column, options }], // options as supabase .order(col, options)
//     limit:     number | null,
//     modifiers: { single, maybeSingle, count, head },
//     values:    any,                   // insert/update/upsert payload
//     upsertOpts:any,                   // optional, for .upsert(values, opts)
//   }
//
// Routing: per-table. Tables in CLOUD_SQL_TABLES send their spec to the
// db-query endpoint (GCF behind the gateway — Cloud SQL); everything else
// delegates to Supabase exactly as before. Flipping a table = adding it here
// AND to the backend allowlist (lib/db-allowlist.js — security review).

import { supabase } from './supabase';
import { apiFetch, API_BASE } from './api';

// ─── THE FLIP LIST. A table here reads/writes Cloud SQL through db-query. ───
const CLOUD_SQL_TABLES = new Set([
  'social_history',
  'medical_history',
  // Phase 2 batch 1 (2026-06-09 draft — deploy db-query + run migrations first)
  'family_history',
  'allergies',
  'problem_list',
  'vitals',
  // Phase 2 batch 2 (2026-06-10 draft — deploy db-query + run migrations first)
  'injections',
  'surgical_history',
  'implant_history',
  'patient_sticky_notes',
  // Phase 2 batch 3 (2026-06-10 draft — deploy db-query + run migrations first)
  'patient_pharmacies',
  // Phase 3 trio (2026-06-10 draft). Unblocked by the portal rehoming:
  // PortalApp now uses apiFetch → portal-chart-data / portal-request-appointment
  // (server-side, portal-session-validated, Cloud SQL direct) instead of
  // touching these tables from the browser. Deploy order matters: data re-sync
  // Supabase→Cloud SQL, prescriptions-dosage migration, db-query +
  // portal-* functions, gateway config — BEFORE this frontend ships.
  'appointments',
  'prescriptions',
  'clinical_notes',
  // Phase 2 batch 4 (2026-06-11 draft — run the 6 batch-4 migrations (incl.
  // the imaging dicom-FK drop) + re-run the isolation seed + redeploy db-query
  // BEFORE this frontend ships).
  'imaging_studies',
  'lab_results',
  'chart_prep_items',
  'order_set_templates',
  // patient_orders + lab_orders (2026-06-11, same batch-4 deploy train).
  // Blockers cleared: PendingOrdersPage join split into two queries; LabsPage
  // PK upsert converted to .update().eq('id'). ALSO requires
  // cloud-sql/migrations/2026-06-11-patient-orders-lab-orders-parity.sql
  // (+ its one-time Supabase surgery_details backfill) before this ships.
  // NOTE: send-fax.js / telnyx-webhook.js still write patient_orders fax
  // columns to SUPABASE — keep the data sync running until those functions
  // are repointed at Cloud SQL, or fax status will go stale in the UI.
  'patient_orders',
  'lab_orders',
  // Phase 2 batch 6 (2026-06-12 draft — run the 7 batch-6 migrations +
  // re-run the isolation seed + redeploy db-query BEFORE this frontend ships).
  // NOT flipped this batch (deliberately absent):
  //   pro_questionnaires / pro_submissions / pro_requests — PortalApp still
  //   touches all three from the browser with the anon client (no JWT);
  //   needs a Phase-3-style portal rehoming first.
  //   medications — table absent from every schema source (;meds phrase
  //   reads it); verify live, then decide repoint-to-prescriptions vs flip.
  'blood_transfusions',
  'consults',
  'consult_favorites',
  'dot_phrases',
  'preference_cards',
  'recent_patients_viewed',
  'favorite_medications',
  'favorite_labs',
  // PRO trio (2026-06-12 — "portal rehoming round 2"). The PROVIDER side flips
  // here (db.from on pro_questionnaires/pro_submissions/pro_requests in
  // OutcomesPage, OutcomesTab, DashboardPage, PatientChartPage — all carry a
  // JWT). The PATIENT-PORTAL side does NOT use db-query: it was rehomed to three
  // server functions that validate token/session and query Cloud SQL directly —
  //   portal-get-questionnaire   (token flow read — repointed to Cloud SQL)
  //   portal-list-questionnaires (NEW — session flow read)
  //   portal-submit-questionnaire(NEW — token+session submit; insert+complete+audit)
  // These THREE functions DEPLOY IN THE SAME TRAIN as this frontend, alongside a
  // redeployed db-query and the gateway routes for the two NEW functions.
  // Prereqs first: 2026-06-12-pro-{questionnaires,submissions,requests}-*.sql in
  // BOTH stores + a Supabase->Cloud SQL re-sync of all three (rows must match) —
  // every provider read + the portal functions read Cloud SQL after the flip.
  // pro_questionnaires is a SELECT-ONLY cross-practice catalog (SYSTEM rows).
  'pro_questionnaires',
  'pro_submissions',
  'pro_requests',
  // patients (2026-06-12 flip — THE core table). Deploy prerequisites, in the
  // SAME deploy train as this frontend:
  //   (0) cloud-sql/migrations/2026-06-12-patients-blood-plan.sql in BOTH
  //       stores (blood_plan jsonb — BloodTransfusionTab wrote a column that
  //       never existed);
  //   (1) REAL patients data sync Supabase → Cloud SQL (row counts must match)
  //       — every flipped table FKs into patients, and ownership checks +
  //       patient search read it;
  //   (2) redeploy db-query (or-group WHERE support + patients allowlist);
  //   (3) repoint the 8 server functions that read/write patients via the
  //       Supabase service key (portal-auth, portal-session-verify,
  //       send-portal-invite, claimmd-check-eligibility, claimmd-submit-claim,
  //       fhir, dosespot-auth, dosespot-sync-patient) — otherwise newly created
  //       patients exist only in Cloud SQL and those functions 404 them.
  // bulk-import-patients / confirm-emr-import stay on Supabase until Phase 3.
  'patients',
  // Billing trio (2026-06-13 — "the easy billing trio"). Deploy in the SAME
  // train as this frontend:
  //   (0) cloud-sql/migrations/2026-06-13-patient-insurances-payer-id-ref.sql in
  //       BOTH stores (idempotent — re-asserts payer_id_ref + the partial-unique
  //       index; no-op if already present). contracted_rates + payers need NO
  //       parity migration (cloud_sql_schema.sql:463/1914 already match).
  //   (1) data re-sync Supabase -> Cloud SQL for all three (patient_insurances
  //       rows, contracted_rates rows, the payers catalog — row counts match).
  //       The provider reads (PayerAutocomplete, insurance forms, fee schedule)
  //       and the charge-ticket rate lookup read Cloud SQL after the flip.
  //   (2) redeploy db-query (billing-trio allowlist).
  // SERVER-FUNCTION dual-store reads to repoint LATER (NOT this batch — the
  // Claim.MD function train): claimmd-check-eligibility reads patient_insurances;
  // claimmd-sync-payers/get-payers/enrollment read/WRITE payers via the service
  // key (browser stays read-only here, so the sync writing to Supabase is fine
  // until repoint); claimmd-submit-claim/generate-cms1500 read contracted_rates.
  // Keep the data sync running until those functions move, or the provider UI
  // and the Claim.MD functions will read divergent stores.
  // payers is a SELECT-ONLY cross-practice catalog (no practice_id column).
  'patient_insurances',
  'contracted_rates',
  'payers',
  // Claim.MD Phase 1 (2026-06-13 — the LOW-RISK independent subset of the
  // Claim.MD cluster). Deploy in the SAME train as this frontend:
  //   (0) cloud-sql/migrations/2026-06-13-eligibility-checks-phase3-cols.sql in
  //       BOTH stores (idempotent — adds the 11 Phase-3 columns the live
  //       Supabase table already has; cloud_sql_schema.sql snapshot was missing
  //       them). provider_enrollments needs NO parity migration
  //       (cloud_sql_schema.sql:2048 already matches).
  //   (1) data re-sync Supabase -> Cloud SQL for both (eligibility_checks history
  //       rows + provider_enrollments rows — counts match). EligibilityCard's
  //       latest-by-verified_at read and EnrollmentManager's list read Cloud SQL
  //       after the flip.
  //   (2) redeploy db-query (Phase-1 allowlist).
  // SERVER-FUNCTION repoints ship in the SAME train (mixed-store, BY DESIGN):
  //   claimmd-check-eligibility  — eligibility_checks (cache read + insert) +
  //     patient_insurances (member_id read, already flipped) -> Cloud SQL;
  //     practices stays Supabase.
  //   claimmd-enrollment         — provider_enrollments (read + upsert) + payers
  //     (payer_name read) -> Cloud SQL; providers + practices stay Supabase.
  //   claimmd-get-payers / claimmd-sync-payers — payers -> Cloud SQL.
  // Those four functions need --update-secrets DB_PASSWORD on deploy (see report).
  // Browser stays read-only on provider_enrollments (writes are server-side via
  // claimmd-enrollment.js); eligibility_checks browser writes are inserts only.
  'eligibility_checks',
  'provider_enrollments',
  // Claim.MD Phase 2 (2026-06-13 — charge_tickets/denials/claimmd_submissions).
  // Deploy in the SAME train as this frontend:
  //   (0) migrations in BOTH stores:
  //       2026-06-13-charge-tickets-phase2-parity.sql (balance cols + visit_date
  //         nullable) and
  //       2026-06-13-claimmd-phase2-denials-submissions-parity.sql (status CHECK
  //         + FK re-assert; idempotent). denials needs no column drift.
  //   (1) data re-sync Supabase -> Cloud SQL for all three (charge_tickets rows,
  //       denials rows, claimmd_submissions rows — counts match). The provider
  //       billing UI (ChargeTicketPage, BillingQueue, ClaimsStatusPage,
  //       ARDashboard, PaymentPostingPage, DenialWorklist) reads Cloud SQL after
  //       the flip.
  //   (2) redeploy db-query (Phase-2 allowlist).
  // SERVER-FUNCTION repoints ship in the SAME train (mixed-store, BY DESIGN):
  //   claimmd-submit-claim   — charge_tickets (read + submission_status update) +
  //     claimmd_submissions (insert + status update) -> Cloud SQL; providers +
  //     practices + practice_integrations stay Supabase.
  //   claimmd-poll-responses — claimmd_submissions (read + status update) +
  //     claimmd_response_events (insert) + charge_tickets (submission_status
  //     update) -> Cloud SQL; claimmd_sync_cursors STAYS Supabase this phase
  //     (it is just a progress watermark; moves to Cloud SQL in Phase 3 with
  //     claimmd-poll-eras). poll-responses is therefore now MIXED (cursor on
  //     Supabase, all claim data on Cloud SQL) — intentional.
  // Those two functions need --update-secrets DB_PASSWORD on deploy (see report).
  // claimmd_response_events is NOT flipped — server-only (no practice_id column;
  // no browser reader). It is written by claimmd-poll-responses via getPool and
  // is deliberately absent from both this list and the db-query allowlist.
  // payments + claimmd_sync_cursors are Phase 3 (with claimmd-poll-eras) — see below.
  'charge_tickets',
  'denials',
  'claimmd_submissions',
  // Claim.MD Phase 3 (2026-06-13 — the FINAL cluster slice; closes Claim.MD).
  // Deploy in the SAME train as this frontend:
  //   (0) migrations in BOTH stores:
  //       2026-06-13-payments-phase3-parity.sql (adds the 7 manually-applied,
  //         never-committed columns era_file/era_file_id/charge_id/source/
  //         posted_at/posted_by/patient_name + re-asserts practice_id/patient_id
  //         TEXT, posting_status default, claimmd_submission_id UUID FK) and
  //       2026-06-13-claimmd-sync-cursors-phase3-parity.sql (idempotent re-assert
  //         of the cursor table — no real drift; anti-fabrication proof).
  //   (1) data re-sync Supabase -> Cloud SQL for payments (rows must match) AND
  //       claimmd_sync_cursors (the per-practice watermark row — copy it so neither
  //       poll function loses its place). PaymentPostingPage's EOB list + the
  //       Dashboard unposted-ERA alert read payments on Cloud SQL after the flip.
  //   (2) redeploy db-query (payments allowlist).
  // SERVER-FUNCTION repoints ship in the SAME train (mixed-store, BY DESIGN):
  //   claimmd-poll-eras   — payments (idempotency read + INSERT) +
  //     claimmd_submissions (claimid/claimmd_id match read; already Phase 2) +
  //     claimmd_sync_cursors (last_eraid read + advance-only upsert) -> Cloud SQL;
  //     practices stays Supabase (creds via getIntegrationCredentials, unchanged).
  //   claimmd-poll-responses — RE-DEPLOY: its claimmd_sync_cursors read/write
  //     (last_responseid) MOVES from Supabase to Cloud SQL this phase. CURSOR
  //     CONSISTENCY: the cursor is ONE shared row per practice (PK=practice_id);
  //     both poll functions must read/write it on the SAME store or the row splits.
  //     poll-responses' claim data was already Cloud SQL (Phase 2); now its cursor
  //     joins it, so poll-responses is fully Cloud SQL after this.
  // Both functions need --update-secrets DB_PASSWORD on deploy (poll-responses
  // already had it from Phase 2; poll-eras is first-time — see report).
  // claimmd_sync_cursors is NOT flipped here — it is SERVER-ONLY (no browser
  // reader, not isolatable via db-query). It is deliberately absent from this list
  // and the db-query allowlist; the two poll functions own it via getPool.
  'payments',
  // Fax/comms provider batch (2026-06-13 — closes the comms/fax cluster). Deploy
  // in the SAME train as this frontend:
  //   (0) cloud-sql/migrations/2026-06-13-fax-comms-parity.sql in BOTH stores
  //       (idempotent re-assert: fax_transmissions practice_id/patient_id TEXT +
  //       the inbound routed_*/media_url/storage_path columns; communications_usage
  //       UNIQUE(practice_id, period); fax_contacts shape). Anti-fabrication proof —
  //       the live schema already matches; the migration documents what the
  //       allowlist + the 3 functions touch.
  //   (1) data re-sync Supabase -> Cloud SQL for fax_transmissions (inbound history
  //       + outbound log), communications_usage (the per-practice/period rows), and
  //       fax_contacts (the address book). FaxPage's inbox + the Dashboard unrouted
  //       count read Cloud SQL after the flip; the 3 functions write Cloud SQL.
  //   (2) redeploy db-query (fax/comms allowlist).
  // SERVER-FUNCTION repoints ship in the SAME train (mixed-store, BY DESIGN):
  //   send-fax.js        — fax_transmissions INSERT + communications_usage upsert
  //     (trackFaxUsage) -> Cloud SQL; practices read + Supabase Storage
  //     (fax-documents bucket) + patient_orders (already Cloud SQL) unchanged.
  //   send-sms.js        — communications_usage upsert (trackSmsUsage) -> Cloud SQL;
  //     sms_transmissions (NOT in scope — stays Supabase) + practices read unchanged.
  //   telnyx-webhook.js  — fax_transmissions upsert/update/select +
  //     communications_usage update -> Cloud SQL; practices read (telnyx_fax_number
  //     match — the cross-store inbound-routing flow) STAYS Supabase;
  //     sms_transmissions delivery update STAYS Supabase; patient_orders already
  //     Cloud SQL. All three need --update-secrets DB_PASSWORD if not already set.
  // communications_usage is SERVER-ONLY (no browser reader) — DELIBERATELY ABSENT
  // from this list and the db-query allowlist; the 3 functions own it via getPool.
  // sms_transmissions + practices STAY Supabase (sms log out of scope; practices is
  // the cross-store anchor). portal_bills/portal_messages/portal_users/intake_forms
  // (Group C) are a SEPARATE batch — untouched here.
  'fax_transmissions',
  'fax_contacts',
  // Group C portal-owned (2026-06-13 — the FINAL data-layer cluster). Only the
  // two PROVIDER-readable tables flip here. Deploy in the SAME train as this
  // frontend:
  //   (0) cloud-sql/migrations/2026-06-13-portal-owned-practice-id.sql in BOTH
  //       stores — SECURITY FIX: adds practice_id TEXT to portal_bills/
  //       portal_users/intake_forms (none had it -> multi-tenancy hole on
  //       RLS-off Cloud SQL) + backfills from patients + re-asserts
  //       intake_forms UNIQUE(patient_id, form_type).
  //   (1) data re-sync Supabase -> Cloud SQL for ALL FOUR portal-owned tables
  //       (portal_messages, portal_bills, portal_users, intake_forms — counts
  //       match). The provider inbox + chart snapshot read portal_messages on
  //       Cloud SQL after the flip; the portal functions read the other three.
  //   (2) redeploy db-query (Group C allowlist).
  // SERVER-FUNCTION repoints ship in the SAME train (mixed-store, BY DESIGN —
  // session validated in Supabase, data on Cloud SQL):
  //   portal-send-message / portal-get-messages — portal_messages -> Cloud SQL.
  //   portal-get-bills (NEW)  — portal_bills read (session-scoped).
  //   portal-user-prefs (NEW) — portal_users read+write (FIXES the latent
  //     .eq('id', user.id) key bug: keys on patient_id = session.patient_id).
  //   portal-submit-intake (NEW) — intake_forms upsert (session-scoped, stamps
  //     practice_id). PortalApp's 6 anon browser call sites move to apiFetch.
  // portal-auth / portal-session-verify do NOT touch portal_users (verified) —
  // no repoint needed there.
  //   portal-get-bills (NEW)  — portal_bills patient-side read (session-scoped).
  // portal_bills ALSO has a PROVIDER reader/writer (PortalManagement
  // PortalBillsTab list + mark-paid), so it is FLIPPED here too. Only portal_users
  // is PORTAL-ONLY (server-function-only) — NOT in this list and NOT in the
  // db-query allowlist (communications_usage / claimmd_sync_cursors precedent).
  // This is the LAST data-layer cluster.
  'portal_messages',
  'intake_forms',
  'portal_bills',
  // Auth-prereq Batch 1 (2026-06-17 — finishing §4 so §5 auth can cut over).
  // DEPLOY ORDER: schema parity + data sync (providers/practices/staff) Supabase →
  // Cloud SQL, then deploy db-query (practiceScopeColumn change), BEFORE this ships.
  // practices scopes on `id` (practiceScopeColumn) — it has no practice_id column.
  // PortalApp's anon practices/providers reads STAY on Supabase (not in db.from).
  'providers',
  'practices',
  'staff',
  // Auth-prereq Batch 2 (2026-06-17 — documents / handouts). PortalApp's anon
  // documents read (PortalApp.jsx ~2217) STAYS on Supabase (portal rehoming, later).
  'documents',
  'custom_handouts',
  'handout_code_overrides',
  // Auth-prereq Batch 3 (2026-06-20 — surgical/ASC cluster, all PHI).
  'surgical_cases',
  'asc_notes',
  'preop_checklists',
  'risk_scores',
  // Auth-prereq Batch 4 (2026-06-20 — comms cluster). `faxes` = legacy fax log,
  // distinct from the already-flipped fax_transmissions. PortalApp uses
  // portal_messages (not `messages`), so nothing portal-side here.
  'messages',
  'tasks',
  'phone_calls',
  'sms_messages',
  'faxes',
  // Auth-prereq Batch 5 (2026-06-20 — billing/meds). `charges` = legacy coding
  // path (distinct from charge_tickets). medication_batches = practice-level templates.
  'charges',
  'denial_appeals',
  'cost_estimates',
  'medication_batches',
  // Auth-prereq Batch 6 (2026-06-20 — scheduling/chart-prep/imaging meta).
  // dicom_studies = metadata (the DICOM objects already moved to GCS).
  'provider_availability',
  'chart_prep_checklists',
  'dicom_studies',
  'appointment_reminders',
  'followup_requests',
  // Auth-prereq Batch 7 (2026-06-20 — trial/import/logging; LAST batch).
  // audit_log: isPHI:false in db-allowlist so the audit insert isn't re-audited.
  'provider_trial_metrics',
  'trial_satisfaction_surveys',
  'import_jobs',
  'feedback',
  'error_log',
  'audit_log',
  // Auth-prereq Batch 8 (2026-06-20 — tail tables the first audit missed).
  // custom_templates flipped 2026-07-07 (see end of list).
  // DEAD (left on supabase): implant_log, practiceQuery.js helpers.
  'dme_catalog',
  'icd10_dme_map',
  'dme_orders',
  'practice_integrations',
  'erx_transmissions',
  'intake_submissions',
  'portal_invites',
  'phrase_favorites',
  // 2026-07-07 — LAST table off Supabase. Redesigned practice-scoped only:
  // cross-practice is_shared reads dropped (built-in templates cover defaults).
  'custom_templates',
]);

// Demo mode has no Supabase session → no JWT for the gateway. Demo keeps the
// Supabase path (its own seed data) — never route it to Cloud SQL.
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

// ─── Read-only mode (subscription lapsed) ───────────────────────────────────
// When ON, write ops are rejected at the single transport chokepoint below, so a
// lapsed practice can still VIEW data but cannot create / edit / delete. Fail-open
// by default (OFF) — only SubscriptionGate flips it on after CONFIRMING an inactive
// subscription, and resets it on unmount. Reads (select) are never affected.
let READ_ONLY = false;
const WRITE_OPS = new Set(['insert', 'update', 'upsert', 'delete']);
// Operational / compliance tables that MUST keep writing even in read-only mode:
// HIPAA access auditing, error logging, and last-viewed tracking are not "edits"
// and continue while a lapsed practice browses charts.
const READ_ONLY_EXEMPT = new Set(['audit_log', 'error_log', 'recent_patients_viewed']);
export function setDbReadOnly(on) { READ_ONLY = !!on; }
export function isDbReadOnly() { return READ_ONLY; }

const FILTER_OPERATORS = new Set([
  'eq', 'neq', 'in', 'gt', 'gte', 'lt', 'lte', 'like', 'ilike', 'is',
]);

// ─── Cloud SQL transport: POST the serializable spec to db-query. ───────────
// Returns the same { data, error, count } shape supabase-js resolves to, so
// call sites cannot tell the difference (PGRST116 semantics included server-side).
async function cloudSqlTransport(spec) {
  try {
    const res = await apiFetch('db-query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(spec),
    });
    try {
      return await res.json(); // { data, error, count } — even on 4xx/5xx
    } catch {
      return { data: null, count: null, error: { message: `db-query: bad response (HTTP ${res.status})` } };
    }
  } catch (e) {
    return { data: null, count: null, error: { message: `db-query: network error — ${e.message}` } };
  }
}

// ─── transport: spec → execution (per-table routing). ───────────────────────
async function transport(spec) {
  // Subscription read-only gate — applies to BOTH the Cloud SQL and Supabase paths.
  // Reads pass through; writes are rejected with a recognizable error shape so call
  // sites surface it like any other failure (no silent data loss).
  if (READ_ONLY && WRITE_OPS.has(spec.op) && !READ_ONLY_EXEMPT.has(spec.table)) {
    return {
      data: null,
      count: null,
      error: {
        message: 'Your subscription is inactive — editing is disabled. Update billing in Settings to restore changes.',
        code: 'SUBSCRIPTION_INACTIVE',
      },
    };
  }
  if (!DEMO_MODE && API_BASE && CLOUD_SQL_TABLES.has(spec.table)) {
    return cloudSqlTransport(spec);
  }
  let q = supabase.from(spec.table);

  switch (spec.op) {
    case 'select':
      q = q.select(spec.columns ?? '*', selectOptions(spec));
      q = applyFilters(q, spec.filters);
      q = applyOrder(q, spec.order);
      if (spec.limit != null) q = q.limit(spec.limit);
      break;

    case 'insert':
      q = q.insert(spec.values);
      if (spec.columns !== undefined) q = q.select(spec.columns ?? '*'); // read-back
      break;

    case 'upsert':
      q = q.upsert(spec.values, spec.upsertOpts);
      if (spec.columns !== undefined) q = q.select(spec.columns ?? '*'); // read-back
      break;

    case 'update':
      q = q.update(spec.values);
      q = applyFilters(q, spec.filters);
      if (spec.columns !== undefined) q = q.select(spec.columns ?? '*'); // read-back
      break;

    case 'delete':
      q = q.delete();
      q = applyFilters(q, spec.filters);
      if (spec.columns !== undefined) q = q.select(spec.columns ?? '*'); // read-back
      break;

    default:
      throw new Error(`db wrapper: unsupported op "${spec.op}"`);
  }

  if (spec.modifiers.single) q = q.single();
  else if (spec.modifiers.maybeSingle) q = q.maybeSingle();

  return q; // a Supabase builder — awaiting it yields { data, error, count, ... }
}

function selectOptions(spec) {
  const opts = {};
  if (spec.modifiers.count) opts.count = spec.modifiers.count;
  if (spec.modifiers.head) opts.head = spec.modifiers.head;
  return Object.keys(opts).length ? opts : undefined;
}

function applyFilters(q, filters) {
  for (const f of filters) {
    if (f.or) { q = q.or(f.raw); continue; } // Supabase path: pass the or-string through
    if (f.negate) { q = q.not(f.col, f.operator, f.value); continue; }
    if (!FILTER_OPERATORS.has(f.operator)) {
      throw new Error(`db wrapper: unsupported filter operator "${f.operator}"`);
    }
    q = q[f.operator](f.col, f.value);
  }
  return q;
}

function applyOrder(q, order) {
  for (const o of order) q = q.order(o.column, o.options);
  return q;
}

// ─── chainable builder. Each method mutates `spec` and returns `this`.
// The builder is a thenable: awaiting it (or calling .then) runs transport once.
class QueryBuilder {
  constructor(table) {
    this.spec = {
      table,
      op: 'select',
      columns: undefined,
      filters: [],
      order: [],
      limit: null,
      modifiers: { single: false, maybeSingle: false, count: null, head: false },
      values: undefined,
      upsertOpts: undefined,
    };
  }

  // .select() doubles as a read op (when first) and a write read-back (after a
  // write op). It never overrides an already-set write op.
  select(columns = '*', opts) {
    this.spec.columns = columns;
    if (opts) {
      if (opts.count) this.spec.modifiers.count = opts.count;
      if (opts.head) this.spec.modifiers.head = opts.head;
    }
    return this;
  }

  insert(values) { this.spec.op = 'insert'; this.spec.values = values; this.spec.columns = undefined; return this; }
  update(values) { this.spec.op = 'update'; this.spec.values = values; return this; }
  upsert(values, upsertOpts) { this.spec.op = 'upsert'; this.spec.values = values; this.spec.upsertOpts = upsertOpts; this.spec.columns = undefined; return this; }
  delete() { this.spec.op = 'delete'; return this; }

  eq(col, value)   { this.spec.filters.push({ col, operator: 'eq', value }); return this; }
  neq(col, value)  { this.spec.filters.push({ col, operator: 'neq', value }); return this; }
  in(col, value)   { this.spec.filters.push({ col, operator: 'in', value }); return this; }
  gt(col, value)   { this.spec.filters.push({ col, operator: 'gt', value }); return this; }
  gte(col, value)  { this.spec.filters.push({ col, operator: 'gte', value }); return this; }
  lt(col, value)   { this.spec.filters.push({ col, operator: 'lt', value }); return this; }
  lte(col, value)  { this.spec.filters.push({ col, operator: 'lte', value }); return this; }
  like(col, value) { this.spec.filters.push({ col, operator: 'like', value }); return this; }
  ilike(col, value){ this.spec.filters.push({ col, operator: 'ilike', value }); return this; }
  is(col, value)   { this.spec.filters.push({ col, operator: 'is', value }); return this; }
  // .not(col, operator, value) → negated filter (e.g. .not('x','is',null))
  not(col, operator, value) { this.spec.filters.push({ col, operator, value, negate: true }); return this; }
  // .or("col.op.value,col.op.value") — supabase or-string, parsed into branches.
  // Each branch is allowlist-validated server-side like any other filter.
  or(orString) {
    const branches = String(orString).split(',').map((part) => {
      const first = part.indexOf('.');
      const second = part.indexOf('.', first + 1);
      return {
        col: part.slice(0, first),
        operator: part.slice(first + 1, second),
        value: part.slice(second + 1),
      };
    });
    this.spec.filters.push({ or: branches, raw: String(orString) });
    return this;
  }

  order(column, options) { this.spec.order.push({ column, options }); return this; }
  limit(n) { this.spec.limit = n; return this; }
  single() { this.spec.modifiers.single = true; return this; }
  maybeSingle() { this.spec.modifiers.maybeSingle = true; return this; }

  // Thenable: makes `await db.from(...)...` and `.then(...)` work like supabase-js.
  // transport() is async and returns the Supabase builder (a thenable), so the
  // promise adopts it and resolves to the final { data, error, count, ... }.
  then(onFulfilled, onRejected) {
    return transport(this.spec).then(onFulfilled, onRejected);
  }
  catch(onRejected) { return this.then(undefined, onRejected); }
  finally(onFinally) { return this.then().finally(onFinally); }
}

export const db = {
  from(table) { return new QueryBuilder(table); },
};

export default db;
